import {
  IonButton,
  IonContent,
  IonInput,
  IonLoading,
  IonModal,
  IonSelect,
  IonSelectOption
} from '@ionic/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  ButtonContainer,
  ModalHeader,
  Padding,
  Spacer
} from '@/components/common';
import { Icon, Text } from '@/components/memo';
import { SelectModal } from '@/components/modals';
import { useDentist } from '@/contexts';
import {
  Service,
  createService,
  deleteService,
  getServiceOptions,
  getServicesByDentistId,
  updateService
} from '@/data/Services';
import { useModal, useToast } from '@/hooks';
import { SERVICE_DURATION_OPTIONS } from '@/utils/constants';
import {
  UseMutationResult,
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query';
import { arrowBackOutline, checkmarkCircleOutline } from 'ionicons/icons';

import './UpdateServices.css';

interface UpdateServicesProps {
  isOpen: boolean;
  onClose: () => void;
  presentingElement?: HTMLElement | undefined;
}

interface CreateServiceInput {
  name: string;
  duration: number;
  dentist_id: string;
}

interface UpdateServiceInput {
  id: string;
  service: Service;
}

const UpdateServices: React.FC<UpdateServicesProps> = ({
  isOpen,
  onClose,
  presentingElement
}) => {
  const displayToast = useToast();
  const { selectedDentist: dentist } = useDentist();
  const queryClient = useQueryClient();

  const [services, setServices] = useState<Service[]>([]);
  const [hasError, setHasError] = useState<boolean>(false);
  const [lastSavedServices, setLastSavedServices] = useState<Service[]>([]);
  const {
    isOpen: isSelectModalOpen,
    onOpen: onOpenSelectModal,
    onClose: onCloseSelectModal
  } = useModal();

  const modal = useRef<HTMLIonModalElement | null>(null);

  const {
    data: initialServices = [],
    isLoading: loading,
    isFetched: loaded
  }: UseQueryResult<Service[], Error> = useQuery({
    queryKey: ['services', dentist?.id],
    queryFn: () => getServicesByDentistId(dentist?.id!),
    enabled: isOpen && !!dentist?.id
  });

  useEffect(() => {
    if (initialServices.length > 0 && loaded) {
      setServices(initialServices);
      setLastSavedServices(initialServices);
    }
  }, [initialServices, loaded]);

  const { data: serviceOptions = [] }: UseQueryResult<string[], Error> =
    useQuery({
      queryKey: ['serviceOptions'],
      queryFn: getServiceOptions,
      enabled: isOpen
    });

  const createServiceMutation: UseMutationResult<
    Service | null,
    Error,
    CreateServiceInput
  > = useMutation({
    mutationFn: (input: CreateServiceInput) => createService(input),
    onError: () => {
      displayToast({
        message: 'Failed to create service. Please try again.',
        duration: 3000,
        position: 'bottom'
      });
    }
  });

  const updateServiceMutation: UseMutationResult<
    Service | null,
    Error,
    UpdateServiceInput
  > = useMutation({
    mutationFn: ({ id, service }: UpdateServiceInput) =>
      updateService(id, service),
    onError: () => {
      displayToast({
        message: 'Failed to update service. Please try again.',
        duration: 3000,
        position: 'bottom'
      });
    }
  });

  const deleteServiceMutation: UseMutationResult<void, Error, string> =
    useMutation({
      mutationFn: deleteService,
      onError: () => {
        displayToast({
          message: 'Failed to delete service. Please try again.',
          duration: 3000,
          position: 'bottom'
        });
      }
    });

  const copy = {
    subHeader: 'SERVICES',
    header: (
      <>
        Manage the <Text color="primary">services</Text> you offer
      </>
    ),
    message: `Please select all the services you offer and indicate the estimated duration of each service.`,
    buttonText: 'Update',
    skipText: 'Skip',
    inputLabel: 'Services',
    inputPlaceholder: 'Example: Braces Adjustment',
    modalTitle: (
      <>
        Select <Text color="primary">services</Text>
      </>
    )
  };

  const isSelected = (value: string) =>
    services.some((service) => service.name === value);

  const isPreSelected = (value: string) =>
    lastSavedServices.some((service) => service.name === value);

  const onSelect = (value: string, selected: boolean) => {
    setServices((prevServices) =>
      selected
        ? prevServices.filter((service) => service.name !== value)
        : [...prevServices, { name: value, duration: 30 }]
    );
  };

  const onCloseSelectModalWithSave = () => {
    onCloseSelectModal();
    setLastSavedServices(services);
  };

  const onDurationChange = (service: Service, value: number) => {
    setServices((prevServices) =>
      prevServices.map((s) =>
        s.name === service.name ? { ...s, duration: value } : s
      )
    );
  };

  const onNext = useCallback(async () => {
    if (services.length > 0 && dentist?.id) {
      setHasError(false);
      try {
        // Update existing services
        const updatePromises = services
          .filter((service) => {
            const existingService = initialServices.find(
              (initialService) => initialService.name === service.name
            );
            return existingService?.id;
          })
          .map(async (service) => {
            const existingService = initialServices.find(
              (initialService) => initialService.name === service.name
            );
            if (!existingService?.id) return;
            return updateServiceMutation.mutateAsync({
              id: existingService.id,
              service: {
                name: service.name,
                duration: service.duration
              }
            });
          });

        await Promise.all(updatePromises);

        // Create new services
        const createPromises = services
          .filter((service) => {
            const existingService = initialServices.find(
              (initialService) => initialService.name === service.name
            );
            return !existingService?.id && dentist?.id;
          })
          .map(async (service) => {
            if (!dentist?.id) return;
            return createServiceMutation.mutateAsync({
              name: service.name,
              duration: service.duration,
              dentist_id: dentist.id
            });
          });

        await Promise.all(createPromises);

        // Delete removed services
        const servicesToDelete = initialServices
          .filter((service) => !services.some((s) => s.name === service.name))
          .filter(
            (service): service is Service & { id: string } =>
              service.id !== null && service.id !== undefined
          );

        const deletePromises = servicesToDelete.map((service) =>
          deleteServiceMutation.mutateAsync(service.id)
        );

        await Promise.all(deletePromises);

        await queryClient.invalidateQueries({
          queryKey: ['services', dentist.id]
        });

        displayToast({
          message: 'Services updated successfully.',
          duration: 3000,
          position: 'bottom',
          positionAnchor: 'tab-bar'
        });
        onClose();
      } catch {
        displayToast({
          message: 'Something went wrong. Please try again.',
          duration: 3000,
          position: 'bottom',
          positionAnchor: 'tab-bar'
        });
      }
    } else {
      setHasError(true);
      displayToast({
        message: 'Please select a valid service.',
        duration: 3000,
        position: 'bottom',
        positionAnchor: 'tab-bar'
      });
    }
  }, [
    services,
    initialServices,
    dentist?.id,
    updateServiceMutation,
    createServiceMutation,
    deleteServiceMutation,
    queryClient,
    onClose
  ]);

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onClose}
      presentingElement={presentingElement}
      ref={modal}
    >
      <IonContent>
        <Padding withSafeArea={!presentingElement}>
          <div className="update-services">
            <ModalHeader
              leftIcon={arrowBackOutline}
              onClickLeftIcon={onClose}
            />
            <Spacer top={16} bottom={24}>
              <h1 className="update-services__header">{copy.header}</h1>
              <p className="update-services__message">{copy.message}</p>
            </Spacer>

            <Spacer bottom={124}>
              <div className="update-services__form">
                <div
                  className={`update-services__input ${hasError ? 'update-services__input--error' : ''}`}
                >
                  <IonInput
                    id="service-input"
                    label={copy.inputLabel}
                    labelPlacement="stacked"
                    value={services.map((service) => service.name).join(', ')}
                    placeholder={copy.inputPlaceholder}
                    onClick={() => {
                      setHasError(false);
                      onOpenSelectModal();
                    }}
                    readonly
                  />
                </div>
              </div>
              {services.length > 0 && (
                <Spacer top={24}>
                  <div className="update-services__selected-services">
                    {services
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((service, index) => (
                        <div
                          key={index}
                          className="update-services__selected-service"
                        >
                          <p className="update-services__service-label">
                            {service.name}
                          </p>
                          <div className="update-services__input">
                            <IonSelect
                              label="Estimated Duration"
                              labelPlacement="stacked"
                              placeholder="Select duration"
                              value={service.duration}
                              onIonChange={(e: CustomEvent) =>
                                onDurationChange(service, e.detail.value)
                              }
                            >
                              {SERVICE_DURATION_OPTIONS.map((option) => (
                                <IonSelectOption
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </IonSelectOption>
                              ))}
                            </IonSelect>
                          </div>
                        </div>
                      ))}
                  </div>
                </Spacer>
              )}
            </Spacer>

            <ButtonContainer>
              <IonButton
                id="next-button"
                onClick={onNext}
                expand="block"
                fill="solid"
                size="default"
                color="primary"
                strong={true}
                disabled={
                  loading ||
                  createServiceMutation.isPending ||
                  updateServiceMutation.isPending ||
                  deleteServiceMutation.isPending
                }
              >
                <Icon slot="start" icon={checkmarkCircleOutline} />
                {copy.buttonText}
              </IonButton>
            </ButtonContainer>
          </div>
        </Padding>
        <IonLoading
          isOpen={
            loading ||
            createServiceMutation.isPending ||
            updateServiceMutation.isPending ||
            deleteServiceMutation.isPending
          }
          message="Loading..."
          spinner="crescent"
        />
        <SelectModal
          isOpen={isSelectModalOpen}
          // TODO: presentingElement is temporarily set to undefined due to an issue with Ionic modal card state when used with IonTabs.
          // Uncomment the line below and remove presentingElement={undefined} once the issue is resolved.
          // presentingElement={modal.current}
          presentingElement={undefined}
          title={copy.modalTitle}
          options={serviceOptions}
          isSelected={isSelected}
          isPreSelected={isPreSelected}
          onSelect={onSelect}
          onClose={onCloseSelectModalWithSave}
          multiple={true}
          showOthers={false}
        />
      </IonContent>
    </IonModal>
  );
};

export default UpdateServices;
